import { PictureOutlined } from "@ant-design/icons";
import { BsReplyFill } from "react-icons/bs";
import { MdFilePresent } from "react-icons/md";
import { conversationType, MESSAGE_STATUS } from "../config/Constant";
import { StickerIcon } from "../config/Icon";
import { extractContent, truncateText } from "./Utils";

const toTime = (v) => {
  if (!v) return 0; // null/"" -> 0
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : 0;
};

const sortConversationList = (conversationList, isSearch = false) => {
  if (!Array.isArray(conversationList) || conversationList.length === 0)
    return [];

  return conversationList
    .filter(Boolean)
    .slice()
    .sort((a, b) => {
      const aPinned = !!a.pinnedConversationTs;
      const bPinned = !!b.pinnedConversationTs;

      // 1) pinned first
      if (aPinned !== bPinned) return aPinned ? -1 : 1;

      // 2) both pinned: newest pin first (optional)
      if (aPinned && bPinned) {
        const diffPin =
          toTime(b.pinnedConversationTs) - toTime(a.pinnedConversationTs);
        if (diffPin !== 0) return diffPin;
      }

      // 3) fallback: lastMessageAt desc

      if (!isSearch) {
        return toTime(b.lastMessageAt) - toTime(a.lastMessageAt);
      } else {
        //dont do something
        return 0;
      }
    });
};

const capitalizeFirstLetter = (text) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : "";

const getDisplayName = (fullName) => {
  if (!fullName) return "";

  const trimmed = fullName.trim();

  const parts = trimmed.split("-").map((p) => p.trim());
  const vietnamesePart = parts[0];

  if (vietnamesePart) {
    return capitalizeFirstLetter(vietnamesePart.split(/\s+/).pop());
  }
  const lastPart = parts.pop();
  return capitalizeFirstLetter(lastPart?.split(/\s+/).pop());
};

const getSenderName = (conversation, user, languageMap) => {
  const isCurrentUser = conversation?.lastSenderId === user?.userId;
  const hasPin = conversation?.lastMessage?.toLowerCase().includes("pin");
  let lastMessageTypeText;
  let lastMessageTypeImage;
  let lastMessageTypeFile;
  if (conversation?.type === conversationType.NOTIFICATION) {
    return;
  } else {
    lastMessageTypeText = conversation?.lastMessageType === MESSAGE_STATUS.TEXT;
    lastMessageTypeImage =
      conversation?.lastMessageType === MESSAGE_STATUS.IMAGE;
    lastMessageTypeFile = conversation?.lastMessageType === MESSAGE_STATUS.FILE;
  }

  if (
    isCurrentUser &&
    !hasPin &&
    (lastMessageTypeText || lastMessageTypeImage || lastMessageTypeFile)
  ) {
    return (
      <span className="text-[#2a56b9] mr-[3px]">
        {languageMap?.["sender.you"] ?? "You:"}
      </span>
    );
  }
};

const getLastMessage = (conversation, user, languageMap) => {
  if (conversation?.lastMessageType === MESSAGE_STATUS.STICKER) {
    return (
      <div className="flex items-center gap-1">
        <StickerIcon width={20} height={20} className="text-[18px]" />
        <span>Sticker</span>
      </div>
    );
  }

  if (conversation?.lastMessageType === MESSAGE_STATUS.IMAGE) {
    return (
      <>
        <PictureOutlined className="mr-1 text-[#666666]" />
        <span>{languageMap?.["modal.conversation.sendPhoto"] ?? "Photo"}</span>
      </>
    );
  }

  if (conversation?.type === "NOTIFICATION") {
    if (conversation?.role === "HOST") {
      const regexText = conversation?.lastMessage
        ?.replace(/<\/?[^>]+(>|$)/g, " ")
        ?.split(", ")
        ?.filter((name) => name !== user?.name)
        ?.join(", ");
      return truncateText(regexText, 30);
    } else {
      return (
        <span className="flex gap-1 items-center">
          {truncateText(conversation?.creatorName, 30)}
        </span>
      );
    }
  }

  if (conversation?.type === "DRAFT") {
    const { text, images } = extractContent(conversation?.lastMessage || "");

    if (text?.trim() === "" && images?.length > 0) {
      return languageMap?.["images"] ?? "images";
    }

    if (text?.trim() === "") {
      return (
        languageMap?.["conversation.lastMessage.draftMessage"] ??
        "Draft message"
      );
    }

    return truncateText(text, 26);
  }

  if (
    ["PROMOTED_MESSAGE", "PINNED_MESSAGE", "UNPINNED_MESSAGE"].includes(
      conversation?.lastMessageType,
    )
  ) {
    return (
      <>
        {truncateText(conversation?.lastMessage, 30)}{" "}
        {languageMap?.[conversation?.lastMessageType] ??
          conversation?.lastMessageType}
      </>
    );
  }

  if (conversation?.lastMessageType === MESSAGE_STATUS.DISABLE_GROUP) {
    return languageMap?.["DISABLE_GROUP"] ?? "";
  }
  if (conversation?.lastMessageType === MESSAGE_STATUS.ENABLE_GROUP) {
    return languageMap?.["ENABLE_GROUP"] ?? "";
  }

  return truncateText(conversation?.lastMessage, 30);
};

const showHasFileIcon = (conversation) => {
  if (
    conversation?.type === conversationType.NOTIFICATION &&
    conversation?.hasFile
  ) {
    return <MdFilePresent size={20} color={"#3269c9"} />;
  }
};

const showHasReplyIcon = (conversation) => {
  if (
    conversation?.type === conversationType.NOTIFICATION &&
    conversation?.hasReply
  ) {
    return <BsReplyFill size={20} color={"#3269c9"} />;
  }
};


export {
  getDisplayName,
  getLastMessage,
  getSenderName,
  showHasFileIcon,
  showHasReplyIcon,
  sortConversationList,
};
